import * as XLSX from 'xlsx';

// Client-side spreadsheet handling for the Project Contact List's bulk import (item 3, 2026-09-12) --
// same shape as externalUserImport.js's Customer Access import: the API never sees the file, only the
// rows this module parses out of it, so there's no upload endpoint and no base64 payload.

export const IMPORT_TEMPLATE_HEADERS = [
  'Name', 'Organization', 'Office', 'Department', 'Role', 'Email', 'Phone',
  'Save as Contact', 'Access to Pulse', 'Access to SharePoint',
];

const EXAMPLE_ROW = [
  'Jane Smith', 'Customer', 'County Seat', 'IT', 'County IT Director', 'jane.smith@example.gov', '(555) 555-0100',
  'Y', 'Y', 'N',
];

/// Triggers a browser download of a starting-point spreadsheet -- one example row, not a blank sheet.
export function downloadImportTemplate() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([IMPORT_TEMPLATE_HEADERS, EXAMPLE_ROW]);
  ws['!cols'] = [
    { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 24 }, { wch: 30 }, { wch: 18 },
    { wch: 16 }, { wch: 16 }, { wch: 20 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Import');
  XLSX.writeFile(wb, 'Project Contact List Import Template.xlsx');
}

const norm = (value) => (value ?? '').toString().trim().toLowerCase();

/// Y/N columns are optional, so a file saved before item 2 existed (or a row that just leaves them
/// blank) has to mean something -- blank keeps "Save as Contact" defaulting to the only behavior the
/// importer had before (always save), while the two access columns default to N, since importing a
/// name/email was never previously a way to ask for portal or SharePoint access.
function parseYN(value, defaultValue) {
  const v = norm(value);
  if (!v) return defaultValue;
  return v === 'y' || v === 'yes' || v === 'true';
}

/// Case/whitespace-tolerant header lookup, not an exact key match -- same reasoning as
/// externalUserImport.js's `field()`.
function field(row, label) {
  const key = Object.keys(row).find((k) => norm(k) === norm(label));
  const value = key ? row[key] : null;
  return value == null ? '' : String(value).trim();
}

/**
 * Reads a File into parsed rows. Resolves to an empty array for a spreadsheet with no data rows;
 * rejects only when the file itself can't be parsed as a spreadsheet at all.
 */
export function readImportFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read this file.'));
    reader.onload = () => {
      try {
        const workbook = XLSX.read(reader.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) return resolve([]);
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: null });
        const rows = raw
          .map((r, i) => ({
            rowNumber: i + 2, // header is row 1
            name: field(r, 'Name'),
            organization: field(r, 'Organization') || field(r, 'Organization'),
            office: field(r, 'Office') || null,
            department: field(r, 'Department') || null,
            role: field(r, 'Role') || null,
            email: field(r, 'Email').toLowerCase() || null,
            phone: field(r, 'Phone') || null,
            saveAsContact: parseYN(field(r, 'Save as Contact'), true),
            accessPulse: parseYN(field(r, 'Access to Pulse'), false),
            accessSharepoint: parseYN(field(r, 'Access to SharePoint'), false),
          }))
          // Drops only a row with nothing in it at all -- the true blank-padding-row case a
          // spreadsheet often has a few of. A row with, say, an Office filled in but no Name isn't
          // silently discarded; it reaches the API and comes back as a "Name is required" row error
          // in the preview grid instead of just vanishing with no explanation.
          .filter((r) => r.name || r.organization || r.office || r.department || r.role || r.email || r.phone);
        resolve(rows);
      } catch {
        reject(new Error('Could not read this file — make sure it is a .xlsx spreadsheet.'));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

/// Strips a preview row (which carries API-computed fields: errors, action) back down to the plain
/// shape `commit` accepts -- the API's ValidationPipe runs with `forbidNonWhitelisted`.
export function toCommitRow(evaluatedRow) {
  const {
    rowNumber, name, organization, office, department, role, email, phone,
    saveAsContact, accessPulse, accessSharepoint,
  } = evaluatedRow;
  return {
    rowNumber, name, organization, office, department, role, email, phone,
    saveAsContact, accessPulse, accessSharepoint,
  };
}

export const ROW_ACTION_LABELS = { create: 'New contact', update: 'Update existing contact', skip: 'Not saved as a contact' };
export const ROW_ACTION_BADGE = { create: 'badge-green', update: 'badge-blue', skip: 'badge-gray' };

export const ACCESS_ACTION_LABELS = {
  request: 'Access requested', 'already-requested': 'Already requested', none: '—',
};
export const ACCESS_ACTION_BADGE = { request: 'badge-green', 'already-requested': 'badge-gray', none: 'badge-gray' };
